import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faCalendarAlt, 
  faFileAlt,
  faChartLine 
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function AdminDashboard() {
  const [stats, setStats] = useState({
    members: 0,
    programs: 0,
    resources: 0,
    payments: 0
  });
  
  const [recentMembers, setRecentMembers] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [membershipTrend, setMembershipTrend] = useState([]);
  const [upcomingPrograms, setUpcomingPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Check if getDashboardStats exists in adminService
        if (typeof adminService.getDashboardStats === 'function') {
          const dashboardData = await adminService.getDashboardStats();
          setStats(dashboardData.stats);
          setRecentMembers(dashboardData.recentMembers);
          setRecentPayments(dashboardData.recentPayments);
          setMembershipTrend(dashboardData.membershipTrend || []);
          setUpcomingPrograms(dashboardData.upcomingPrograms || []);
        } else {
          // Mock data for development/testing
          console.log('Using mock dashboard data as getDashboardStats is not implemented');
          const mockData = {
            stats: {
              members: 128,
              programs: 12,
              resources: 45,
              payments: 87
            },
            recentMembers: [
              { id: 1, firstName: 'John', lastName: 'Doe', membershipType: 'Lifetime', createdAt: '2023-10-15T10:30:00', status: 'active', paymentStatus: 'paid' },
              { id: 2, firstName: 'Jane', lastName: 'Smith', membershipType: 'Annual', createdAt: '2023-10-10T14:20:00', status: 'pending', paymentStatus: 'unpaid' },
              { id: 3, firstName: 'Raj', lastName: 'Kumar', membershipType: 'Lifetime', createdAt: '2023-09-28T09:15:00', status: 'active', paymentStatus: 'paid' }
            ],
            recentPayments: [
              { id: 1, memberName: 'John Doe', amount: 5000, date: '2023-10-15T10:30:00', paymentType: 'Lifetime Membership', status: 'completed' },
              { id: 2, memberName: 'Jane Smith', amount: 1500, date: '2023-10-10T14:20:00', paymentType: 'Annual Membership', status: 'failed' },
              { id: 3, memberName: 'Raj Kumar', amount: 5000, date: '2023-09-28T09:15:00', paymentType: 'Donation', status: 'pending' }
            ],
            membershipTrend: [
              { month: '2023-04', count: 5 },
              { month: '2023-05', count: 8 },
              { month: '2023-06', count: 12 },
              { month: '2023-07', count: 7 },
              { month: '2023-08', count: 10 },
              { month: '2023-09', count: 15 },
              { month: '2023-10', count: 9 }
            ],
            upcomingPrograms: [
              { id: 1, name: 'Annual General Meeting', date: '2023-12-15T10:00:00', registrations: 45, location: 'Main Hall' },
              { id: 2, name: 'Career Guidance Workshop', date: '2023-11-20T14:00:00', registrations: 32, location: 'Conference Room B' },
              { id: 3, name: 'Alumni Networking Event', date: '2023-12-05T18:30:00', registrations: 78, location: 'Banquet Hall' }
            ]
          };
          setStats(mockData.stats);
          setRecentMembers(mockData.recentMembers);
          setRecentPayments(mockData.recentPayments);
          setMembershipTrend(mockData.membershipTrend);
          setUpcomingPrograms(mockData.upcomingPrograms);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Fallback to mock data on error
        const mockData = {
          stats: { members: 120, programs: 10, resources: 40, payments: 80 },
          recentMembers: [],
          recentPayments: [],
          membershipTrend: [],
          upcomingPrograms: []
        };
        setStats(mockData.stats);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get payment status styling
  const getPaymentStatusStyle = (status) => {
    const styles = {
      completed: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' },
      pending: { backgroundColor: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' },
      failed: { backgroundColor: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }
    };
    return styles[status] || styles.pending;
  };

  // Get member status styling
  const getMemberStatusStyle = (status, paymentStatus) => {
    if (paymentStatus === 'unpaid') {
      return { backgroundColor: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' };
    }
    const styles = {
      active: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' },
      pending: { backgroundColor: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' },
      inactive: { backgroundColor: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }
    };
    return styles[status] || styles.pending;
  };

  // Get display status text
  const getDisplayStatus = (status, paymentStatus) => {
    if (paymentStatus === 'unpaid') {
      return 'Payment Failed';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Prepare chart data from membership trend
  const getChartData = () => {
    // Sort data by month to ensure chronological order
    const sortedData = [...membershipTrend].sort((a, b) => new Date(a.month) - new Date(b.month));
    
    // Format month labels
    const labels = sortedData.map(item => {
      const [year, month] = item.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    });
    
    const data = sortedData.map(item => item.count);
    
    return {
      labels,
      datasets: [
        {
          label: 'New Members',
          data,
          fill: false,
          backgroundColor: '#002147',
          borderColor: '#002147',
          tension: 0.1
        }
      ]
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Dashboard</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Dashboard</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-dashboard-cards">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ backgroundColor: '#002147' }}>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="admin-stat-info">
            <h3>{stats.members}</h3>
            <p>Total Members</p>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ backgroundColor: '#28a745' }}>
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
          <div className="admin-stat-info">
            <h3>{stats.programs}</h3>
            <p>Programs</p>
          </div>
        </div>
        
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ backgroundColor: '#f7c936' }}>
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div className="admin-stat-info">
            <h3>{stats.payments}</h3>
            <p>Payments</p>
          </div>
        </div>
      </div>
      
      {/* Upcoming Programs Cards Row */}
      <div className="admin-card" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="admin-card-header" style={{ borderBottom: 'none', paddingBottom: '0.5rem' }}>
          <h2 className="admin-card-title">Upcoming Programs</h2>
          <a href="/admin/programs" className="admin-link">View All</a>
        </div>
        <div className="admin-card-body" style={{ padding: '0.5rem 1rem 1.5rem' }}>
          {isLoading ? (
            <div style={{ padding: '1.5rem', textAlign: 'center' }}>Loading upcoming programs...</div>
          ) : upcomingPrograms.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'flex-start' }}>
              {upcomingPrograms.slice(0, 4).map(program => (
                <div key={program.id} style={{ 
                  flex: '1 1 220px',
                  maxWidth: 'calc(25% - 12px)',
                  padding: '15px',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                }}
                onClick={() => window.location.href = `/admin/programs/${program.id}`}>
                  <div style={{ fontWeight: 'bold', fontSize: '1rem', marginBottom: '8px', color: '#002147' }}>
                    {program.name}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '10px' }}>
                    <div><FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px', fontSize: '0.8rem' }}/>{formatDate(program.date)}</div>
                    <div style={{ marginTop: '5px' }}>{program.location}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '5px' }}>
                      {program.registrations} registrations
                    </div>
                    <div style={{ backgroundColor: '#e9ecef', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div 
                        style={{ 
                          width: `${Math.min(program.registrations, 100)}%`, 
                          backgroundColor: '#002147', 
                          height: '100%'
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              No upcoming programs scheduled
            </div>
          )}
        </div>
      </div>
      
      <div className="admin-row" style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -0.75rem' }}>
        <div className="admin-col" style={{ flex: '1 1 50%', padding: '0 0.75rem', minWidth: '300px' }}>
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">Recent Members</h2>
              <a href="/admin/members" className="admin-link">View All</a>
            </div>
            <div className="admin-card-body" style={{ padding: '0' }}>
              {isLoading ? (
                <div style={{ padding: '1.5rem', textAlign: 'center' }}>Loading...</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentMembers.length > 0 ? (
                      recentMembers.map((member) => (
                        <tr key={member.id}>
                          <td>{member.firstName} {member.lastName}</td>
                          <td>{member.membershipType}
                          < br />
                            <span style={getMemberStatusStyle(member.status, member.paymentStatus)}>
                              {getDisplayStatus(member.status, member.paymentStatus)}
                            </span>
                          </td>
                          <td>{formatDate(member.createdAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center' }}>No members found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        
        <div className="admin-col" style={{ flex: '1 1 50%', padding: '0 0.75rem', minWidth: '300px' }}>
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">Recent Payments</h2>
              <a href="/admin/payments" className="admin-link">View All</a>
            </div>
            <div className="admin-card-body" style={{ padding: '0' }}>
              {isLoading ? (
                <div style={{ padding: '1.5rem', textAlign: 'center' }}>Loading...</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Amount</th>
                      <th>Payment Type</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.length > 0 ? (
                      recentPayments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{payment.memberName}</td>
                          <td>₹{payment.amount}</td>
                          <td>{payment.type || payment.paymentType || 'N/A'}
                            < br />
                            <span style={getPaymentStatusStyle(payment.status)}>
                              {payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'Pending'}
                            </span>
                          </td>
                          <td>{formatDate(payment.date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center' }}>No payments found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-card" style={{ marginTop: '1.5rem' }}>
        <div className="admin-card-header">
          <h2 className="admin-card-title">Membership Overview</h2>
          <div>
            <FontAwesomeIcon icon={faChartLine} style={{ color: 'var(--admin-primary)', marginRight: '0.5rem' }} />
            <span>Last 12 months</span>
          </div>
        </div>
        <div className="admin-card-body" style={{ height: '300px' }}>
          {isLoading ? (
            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <p>Loading chart data...</p>
            </div>
          ) : membershipTrend.length > 0 ? (
            <Line data={getChartData()} options={chartOptions} />
          ) : (
            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <p>No membership data available for the chart</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard;
